import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, Calendar, Users, TrendingUp, Clock, DollarSign } from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';

const ProducersBuddyMarketing = () => {
  // Animation states
  const [progressAnimation, setProgressAnimation] = useState(false);
  const [castAnimation, setCastAnimation] = useState(false);
  const [scheduleAnimation, setSchedululeAnimation] = useState(false);

  // Trigger animations on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      if (scrollPosition > 100) {
        setProgressAnimation(true);
      }
      if (scrollPosition > 500) {
        setCastAnimation(true);
      }
      if (scrollPosition > 900) {
        setSchedululeAnimation(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Benefits data
  const benefits = [
    { icon: <Clock className="text-blue-500" />, text: "Save 40+ hours per production" },
    { icon: <DollarSign className="text-green-500" />, text: "Reduce production costs by up to 30%" },
    { icon: <TrendingUp className="text-purple-500" />, text: "100% Scalable across projects" }
  ];

  return (
    <>
    <SEO
      title="Production Stage Advertising"
      description="Market your film during production with targeted advertising strategies and tools on ProducerBuddy."
      url="https://producerbuddy.pilomation.com/production-advertising"
      robots="noindex, nofollow"
    />
    <Nav />
    <div id="producers-buddy-wrapper" className="w-full bg-gradient-to-b from-gray-900 to-blue-900 text-gray-800 relative overflow-hidden">
      {/* Background Animation Elements */}
      <div className="animated-bg">
        <div className="bg-orb orb-1"></div>
        <div className="bg-orb orb-2"></div>
        <div className="bg-orb orb-3"></div>
        <div className="bg-orb orb-4"></div>
        <div className="bg-orb orb-5"></div>
        <div className="bg-lines"></div>
      </div>
      
      {/* Content layer */}
      <div className="relative z-10">
        {/* Hero Section */}
        <div className="max-w-6xl mx-auto py-20 px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-teal-400 to-purple-600">
              Transform Your Production Process with AI
            </h1>
            <p className="text-xl text-gray-100 max-w-3xl mx-auto">
              Producer's Buddy brings cutting-edge AI tools to revolutionize film production, 
              saving time, effort, and budget while scaling your creative vision.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center bg-gray-800 bg-opacity-50 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-teal-500 border-opacity-20 hover:border-opacity-40 transition-all">
                  {benefit.icon}
                  <span className="ml-3 text-lg text-white">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Progress Management Tool */}
          <div className="mb-32">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <div className="inline-block bg-blue-500 text-sm font-semibold px-3 py-1 rounded-full mb-3 text-white">
                  Tool #1
                </div>
                <h2 className="text-3xl font-bold mb-4 text-white">Progress Management</h2>
                <p className="text-gray-300 mb-6">
                  Track every aspect of your production in real-time with AI-powered analytics. 
                  Visualize bottlenecks before they happen and optimize your workflow dynamically.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Real-time dashboards with AI-predicted completion timelines</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Automated progress reports and stakeholder updates</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Resource allocation intelligence to prevent overruns</span>
                  </li>
                </ul>
                
                <a 
                  href="/services" 
                  className="inline-flex items-center bg-gradient-to-r from-blue-600 via-teal-500 to-purple-600 text-white py-3 px-6 rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-teal-500/20"
                >
                  Get Early Access (20% Off)
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
              
              <div className="md:w-1/2 mt-8 md:mt-0">
                <div id="progress-tool" className={`relative bg-gray-800 bg-opacity-70 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-teal-500 border-opacity-10 ${progressAnimation ? 'animate-fadeIn' : 'opacity-0'}`}>
                  <div className="relative z-10">
                    {/* Progress Tool Animation */}
                    <div className="bg-gray-900 bg-opacity-80 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-lg text-white">Production Progress</h3>
                        <span className="text-green-400">76% Complete</span>
                      </div>
                      
                      {/* Progress bars */}
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-1 text-sm">
                            <span className="text-white">Pre-Production</span>
                            <span className="text-white">100%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full">
                            <div className="h-full bg-green-500 rounded-full w-full"></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1 text-sm">
                            <span className="text-white">Principal Photography</span>
                            <span className="text-white">68%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full">
                            <div className="h-full bg-blue-500 rounded-full w-2/3"></div>
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-1 text-sm">
                            <span className="text-white">Post-Production</span>
                            <span className="text-white">12%</span>
                          </div>
                          <div className="h-2 bg-gray-700 rounded-full">
                            <div className="h-full bg-purple-500 rounded-full w-1/12"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6 bg-blue-900 bg-opacity-30 p-4 rounded-lg border border-blue-700">
                        <div className="flex items-start">
                          <div className="bg-blue-600 p-2 rounded-lg mr-3">
                            <TrendingUp className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-400">AI Insight</h4>
                            <p className="text-sm text-white">Based on current progress, we forecast completion 3 days ahead of schedule. Consider reallocating resources from Scene 14 to Scene 22 to optimize crew efficiency.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Animated elements */}
                  <div className="absolute top-1/4 right-4 w-16 h-16 bg-blue-500 rounded-full opacity-20 animate-pulse"></div>
                  <div className="absolute bottom-1/4 left-8 w-12 h-12 bg-teal-500 rounded-full opacity-10 animate-ping"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Cast Management Tool */}
          <div className="mb-32">
            <div className="flex flex-col md:flex-row-reverse items-center gap-8">
              <div className="md:w-1/2">
                <div className="inline-block bg-purple-500 text-sm font-semibold px-3 py-1 rounded-full mb-3 text-white">
                  Tool #2
                </div>
                <h2 className="text-3xl font-bold mb-4 text-white">Cast Management</h2>
                <p className="text-gray-300 mb-6">
                  Streamline talent coordination with AI-powered casting tools that optimize actor schedules,
                  track availability, and intelligently manage conflicts.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Smart conflict resolution for overlapping talent needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Automated call sheet generation and distribution</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Real-time talent availability tracking and notifications</span>
                  </li>
                </ul>
                
                <a 
                  href="/services" 
                  className="inline-flex items-center bg-gradient-to-r from-purple-600 via-teal-500 to-pink-600 text-white py-3 px-6 rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-teal-500/20"
                >
                  Reserve Your Spot (15% Off)
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
              
              <div className="md:w-1/2 mt-8 md:mt-0">
                <div id="cast-tool" className={`relative bg-gray-800 bg-opacity-70 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-teal-500 border-opacity-10 ${castAnimation ? 'animate-fadeIn' : 'opacity-0'}`}>
                  <div className="relative z-10">
                    {/* Cast Management Animation */}
                    <div className="bg-gray-900 bg-opacity-80 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-lg text-white">Cast Availability</h3>
                        <span className="text-purple-400 flex items-center">
                          <Users className="h-4 w-4 mr-1" /> 
                          24 Talents
                        </span>
                      </div>
                      
                      {/* Cast availability tracker */}
                      <div className="space-y-3">
                        <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-lg font-bold mr-3 text-white">
                              JD
                            </div>
                            <div>
                              <div className="font-medium text-white">James Davis</div>
                              <div className="text-xs text-gray-400">Lead Role - Alex</div>
                            </div>
                          </div>
                          <div className="bg-green-500 bg-opacity-20 text-green-400 text-xs px-2 py-1 rounded">Available</div>
                        </div>
                        
                        <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-lg font-bold mr-3 text-white">
                              ML
                            </div>
                            <div>
                              <div className="font-medium text-white">Maria Lopez</div>
                              <div className="text-xs text-gray-400">Supporting - Sarah</div>
                            </div>
                          </div>
                          <div className="bg-yellow-500 bg-opacity-20 text-yellow-400 text-xs px-2 py-1 rounded">Limited</div>
                        </div>
                        
                        <div className="bg-gray-800 p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-pink-600 flex items-center justify-center text-lg font-bold mr-3 text-white">
                              RK
                            </div>
                            <div>
                              <div className="font-medium text-white">Rachel Kim</div>
                              <div className="text-xs text-gray-400">Supporting - Eliza</div>
                            </div>
                          </div>
                          <div className="bg-red-500 bg-opacity-20 text-red-400 text-xs px-2 py-1 rounded">Conflict</div>
                        </div>
                      </div>
                      
                      <div className="mt-6 bg-purple-900 bg-opacity-30 p-4 rounded-lg border border-purple-700">
                        <div className="flex items-start">
                          <div className="bg-purple-600 p-2 rounded-lg mr-3">
                            <Users className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-purple-400">AI Recommendation</h4>
                            <p className="text-sm text-white">Scheduling conflict detected with Rachel Kim. AI suggests rescheduling Scene 17 to June 4th or replacing with Scene 23 (no conflicts) to avoid production delay.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Animated elements */}
                  <div className="absolute top-1/3 left-6 w-14 h-14 bg-purple-500 rounded-full opacity-20 animate-pulse"></div>
                  <div className="absolute bottom-1/3 right-10 w-10 h-10 bg-teal-500 rounded-full opacity-10 animate-ping"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Scheduling Assistant Tool */}
          <div className="mb-20">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <div className="inline-block bg-green-500 text-sm font-semibold px-3 py-1 rounded-full mb-3 text-white">
                  Tool #3
                </div>
                <h2 className="text-3xl font-bold mb-4 text-white">Scheduling Assistant</h2>
                <p className="text-gray-300 mb-6">
                  Our AI-powered scheduling engine analyzes thousands of variables to create 
                  the perfect production schedule, adapting in real-time to changes and minimizing costly delays.
                </p>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Weather-adaptive scheduling with predictive AI</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Dynamic rescheduling to minimize downtime costs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-white">Location and equipment optimization algorithms</span>
                  </li>
                </ul>
                
                <a 
                  href="/services" 
                  className="inline-flex items-center bg-gradient-to-r from-green-600 via-teal-500 to-teal-600 text-white py-3 px-6 rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-teal-500/20"
                >
                  Pre-order Now (25% Off)
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
              
              <div className="md:w-1/2 mt-8 md:mt-0">
                <div id="schedule-tool" className={`relative bg-gray-800 bg-opacity-70 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-teal-500 border-opacity-10 ${scheduleAnimation ? 'animate-fadeIn' : 'opacity-0'}`}>
                  <div className="relative z-10">
                    {/* Scheduling Assistant Animation */}
                    <div className="bg-gray-900 bg-opacity-80 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-6">
                        <h3 className="font-bold text-lg text-white">Smart Scheduling</h3>
                        <span className="text-green-400 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" /> 
                          Next 14 Days
                        </span>
                      </div>
                      
                      {/* Schedule visualization */}
                      <div className="grid grid-cols-7 gap-1 mb-4">
                        {[...Array(7)].map((_, i) => (
                          <div key={i} className="text-center text-xs text-gray-400">
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i]}
                          </div>
                        ))}
                        
                        {[...Array(14)].map((_, i) => {
                          const colorClass = 
                            i === 2 || i === 9 ? "bg-red-500 bg-opacity-30" :
                            i === 3 || i === 4 || i === 10 ? "bg-green-500 bg-opacity-30" :
                            i === 5 || i === 11 ? "bg-blue-500 bg-opacity-30" :
                            "bg-gray-700";
                          
                          return (
                            <div 
                              key={i} 
                              className={`h-12 rounded ${colorClass} flex items-center justify-center text-xs relative ${i % 7 === 0 && i > 0 ? 'col-start-1' : ''} text-white`}
                            >
                              {i + 1}
                              {(i === 2 || i === 9) && <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-ping"></div>}
                            </div>
                          );
                        })}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 text-xs mb-4">
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-green-500 bg-opacity-30 rounded mr-1"></div>
                          <span className="text-white">On Location</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-blue-500 bg-opacity-30 rounded mr-1"></div>
                          <span className="text-white">Studio</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-3 h-3 bg-red-500 bg-opacity-30 rounded mr-1"></div>
                          <span className="text-white">Warning</span>
                        </div>
                      </div>
                      
                      <div className="mt-4 bg-green-900 bg-opacity-30 p-4 rounded-lg border border-green-700">
                        <div className="flex items-start">
                          <div className="bg-green-600 p-2 rounded-lg mr-3">
                            <Calendar className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-green-400">AI Schedule Alert</h4>
                            <p className="text-sm text-white">Weather forecast indicates 78% chance of rain on days 3 and 10. AI has automatically rescheduled outdoor scenes and optimized indoor shooting to prevent $14,500 in potential downtime costs.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Animated elements */}
                  <div className="absolute top-1/4 left-10 w-12 h-12 bg-green-500 rounded-full opacity-20 animate-pulse"></div>
                  <div className="absolute bottom-1/4 right-6 w-16 h-16 bg-teal-500 rounded-full opacity-10 animate-ping"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* ROI and Final CTA Section */}
          <div id="cta-section" className="bg-gray-800 bg-opacity-50 rounded-2xl p-8 text-center backdrop-blur-sm border border-teal-500 border-opacity-10">
            <h2 className="text-3xl font-bold mb-6 text-white">Transform Your Production Workflow</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Producer's Buddy AI tools have helped production houses save an average of 
              <span className="text-green-400 font-bold"> 32% on production costs</span> while 
              delivering projects <span className="text-blue-400 font-bold">28% faster</span>.
            </p>
            
            <div className="max-w-xl mx-auto mb-10 bg-gray-900 bg-opacity-70 p-6 rounded-xl border border-teal-500 border-opacity-10">
              <h3 className="text-xl font-semibold mb-4 text-white">Limited Time Early Access</h3>
              <div className="flex justify-between items-center mb-3">
                <span className="text-lg text-white">Standard Price</span>
                <span className="text-lg line-through text-gray-400">$299/month</span>
              </div>
              <div className="flex justify-between items-center mb-6">
                <span className="text-lg font-bold text-white">Early Access Price</span>
                <span className="text-2xl font-bold text-green-400">$199/month</span>
              </div>
              <a 
                href="/services" 
                className="block w-full bg-gradient-to-r from-blue-600 via-teal-500 to-purple-600 text-white py-4 px-6 rounded-lg font-medium text-lg transition-all hover:scale-105 hover:shadow-lg hover:shadow-teal-500/20"
              >
                Get Early Access Now
              </a>
              <p className="text-sm text-gray-400 mt-3">Limited slots available. Offer ends soon.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Add custom styles for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out forwards;
        }
        
        /* Background animation styles */
        .animated-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          pointer-events: none;
        }
        
        .bg-orb {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
        }
        
        .orb-1 {
          top: 5%;
          left: 10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(45,212,191,1) 0%, rgba(56,189,248,0) 70%);
          animation: float 20s ease-in-out infinite;
        }
        
        .orb-2 {
          top: 30%;
          right: 5%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(20,184,166,1) 0%, rgba(56,189,248,0) 70%);
          animation: float 25s ease-in-out infinite reverse;
        }
        
        .orb-3 {
          bottom: 10%;
          left: 20%;
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, rgba(14,165,233,1) 0%, rgba(56,189,248,0) 70%);
          animation: float 18s ease-in-out infinite 2s;
        }
        
        .orb-4 {
          top: 60%;
          right: 25%;
          width: 380px;
          height: 380px;
          background: radial-gradient(circle, rgba(168,85,247,1) 0%, rgba(56,189,248,0) 70%);
          animation: float 22s ease-in-out infinite 1s;
        }
        
        .orb-5 {
          top: 50%;
          left: 50%;
          width: 600px;
          height: 600px;
          background: radial-gradient(circle, rgba(13,148,136,1) 0%, rgba(56,189,248,0) 80%);
          animation: float 30s ease-in-out infinite 3s;
        }
        
        .bg-lines {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: linear-gradient(0deg, transparent 24%, rgba(13,148,136,0.05) 25%, rgba(13,148,136,0.05) 26%, transparent 27%, transparent 74%, rgba(13,148,136,0.05) 75%, rgba(13,148,136,0.05) 76%, transparent 77%, transparent),
                            linear-gradient(90deg, transparent 24%, rgba(13,148,136,0.05) 25%, rgba(13,148,136,0.05) 26%, transparent 27%, transparent 74%, rgba(13,148,136,0.05) 75%, rgba(13,148,136,0.05) 76%, transparent 77%, transparent);
          background-size: 50px 50px;
        }
        
        @keyframes float {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.05);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.95);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default ProducersBuddyMarketing;
